import { useEffect, useRef, useState } from "react";
import styles from "../css/LearningTimeLimitView.module.css";

// 남은 시간이 이 값 이하로 내려가면 경고 색상이 시작된다.
const WARNING_SECOND_MAX = 3;
const VIEW_SIZE = 32;
const LINE_WIDTH = 2;
// iOS, Android 와 동일한 반지름(innerRadius 13, thickness 2)을 사용한다.
const RADIUS = (VIEW_SIZE - LINE_WIDTH) / 2 - 1;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface TimeLimitColors {
  track: Rgba;
  controlActive: Rgba;
  textSecondary: Rgba;
  orange: Rgba;
  red: Rgba;
}

interface LearningTimeLimitViewProps {
  // 0 이면 제한 시간을 사용하지 않는다.
  limitSecond: number;
  // 값이 바뀔 때마다 제한 시간이 처음부터 다시 시작된다.
  restartToken: number;
  // 정답을 확인한 뒤에는 제한 시간을 멈춘다.
  isStopped: boolean;
  // 로딩 중이거나 설정 화면이 열려있는 동안에는 제한 시간이 흐르지 않도록 한다.
  isPaused: boolean;
  onTimeout: () => void;
}

export default function LearningTimeLimitView(
  props: LearningTimeLimitViewProps
) {
  const [remainSecond, setRemainSecond] = useState(props.limitSecond);
  const [colors, setColors] = useState<TimeLimitColors>(getColors);

  const remainSecondRef = useRef(props.limitSecond);
  const lastTimestampRef = useRef(0);
  const isPausedRef = useRef(props.isPaused);
  const onTimeoutRef = useRef(props.onTimeout);

  useEffect(() => {
    isPausedRef.current = props.isPaused;
  }, [props.isPaused]);

  useEffect(() => {
    onTimeoutRef.current = props.onTimeout;
  }, [props.onTimeout]);

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setColors(getColors());

    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (props.limitSecond <= 0 || props.isStopped === true) return;

    remainSecondRef.current = props.limitSecond;
    lastTimestampRef.current = 0;
    setRemainSecond(props.limitSecond);

    let frameID = requestAnimationFrame(step);

    function step(timestamp: number) {
      frameID = requestAnimationFrame(step);

      // 멈춰있는 동안 흐른 시간은 제외한다.
      if (isPausedRef.current === true) {
        lastTimestampRef.current = 0;
        return;
      }

      // 첫 프레임은 기준 시각만 잡는다.
      if (lastTimestampRef.current === 0) {
        lastTimestampRef.current = timestamp;
        return;
      }

      const delta = (timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;
      if (delta <= 0) return;

      const nextRemainSecond = Math.max(0, remainSecondRef.current - delta);
      remainSecondRef.current = nextRemainSecond;
      setRemainSecond(nextRemainSecond);

      if (nextRemainSecond > 0) return;

      cancelAnimationFrame(frameID);
      onTimeoutRef.current();
    }

    return () => cancelAnimationFrame(frameID);
  }, [props.limitSecond, props.restartToken, props.isStopped]);

  if (props.limitSecond <= 0 || props.isStopped === true) return null;

  const warningSecond = Math.min(WARNING_SECOND_MAX, props.limitSecond);
  const ringColor = getTimeLimitColor(
    colors.controlActive,
    colors,
    remainSecond,
    warningSecond
  );
  const textColor = getTimeLimitColor(
    colors.textSecondary,
    colors,
    remainSecond,
    warningSecond
  );
  // 표시는 남은 초를 올림해서 마지막 구간까지 1을 유지한다.
  const displaySecond = Math.max(1, Math.ceil(remainSecond));

  return (
    <div className={styles.container}>
      <svg
        className={styles.ring}
        width={VIEW_SIZE}
        height={VIEW_SIZE}
        viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
      >
        <circle
          cx={VIEW_SIZE / 2}
          cy={VIEW_SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={LINE_WIDTH}
          stroke={toColorString(colors.track)}
        />
        <circle
          cx={VIEW_SIZE / 2}
          cy={VIEW_SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={LINE_WIDTH}
          strokeLinecap="butt"
          stroke={toColorString(ringColor)}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={
            CIRCUMFERENCE * (1 - remainSecond / props.limitSecond)
          }
        />
      </svg>
      <span className={styles.countText} style={{ color: toColorString(textColor) }}>
        {displaySecond}
      </span>
    </div>
  );
}

function getTimeLimitColor(
  baseColor: Rgba,
  colors: TimeLimitColors,
  remainSecond: number,
  warningSecond: number
): Rgba {
  if (warningSecond <= 0 || remainSecond > warningSecond) return baseColor;

  const fraction = (warningSecond - remainSecond) / warningSecond;
  if (fraction <= 0.5) {
    return interpolateColor(baseColor, colors.orange, fraction * 2);
  }
  return interpolateColor(colors.orange, colors.red, (fraction - 0.5) * 2);
}

function getColors(): TimeLimitColors {
  return {
    track: getColorFromCSSVariable("--border-secondary-color"),
    controlActive: getColorFromCSSVariable("--control-active-color"),
    textSecondary: getColorFromCSSVariable("--text-secondary-color"),
    orange: getColorFromCSSVariable("--icon-orange-color"),
    red: getColorFromCSSVariable("--icon-red-color"),
  };
}

function getColorFromCSSVariable(name: string): Rgba {
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    name
  );
  return parseColor(value);
}

function parseColor(value: string): Rgba {
  const color = value.trim();

  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const expanded =
      hex.length === 3 || hex.length === 4
        ? hex
            .split("")
            .map((character) => character + character)
            .join("")
        : hex;

    return {
      r: parseInt(expanded.slice(0, 2), 16),
      g: parseInt(expanded.slice(2, 4), 16),
      b: parseInt(expanded.slice(4, 6), 16),
      a:
        expanded.length === 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1,
    };
  }

  const numbers = color.match(/-?\d*\.?\d+/g);
  if (numbers !== null && numbers.length >= 3) {
    return {
      r: Number(numbers[0]),
      g: Number(numbers[1]),
      b: Number(numbers[2]),
      a: numbers.length >= 4 ? Number(numbers[3]) : 1,
    };
  }

  return { r: 0, g: 0, b: 0, a: 1 };
}

// Android ArgbEvaluator 와 동일하게 sRGB -> linear 로 변환한 뒤 보간한다.
function interpolateColor(from: Rgba, to: Rgba, fraction: number): Rgba {
  const value = Math.min(Math.max(fraction, 0), 1);
  const gamma = 2.2;

  function blend(fromValue: number, toValue: number): number {
    const fromLinear = Math.pow(fromValue / 255, gamma);
    const toLinear = Math.pow(toValue / 255, gamma);
    return Math.pow(fromLinear + value * (toLinear - fromLinear), 1 / gamma) * 255;
  }

  return {
    r: blend(from.r, to.r),
    g: blend(from.g, to.g),
    b: blend(from.b, to.b),
    a: from.a + value * (to.a - from.a),
  };
}

function toColorString(color: Rgba): string {
  const r = Math.round(color.r);
  const g = Math.round(color.g);
  const b = Math.round(color.b);
  return `rgba(${r}, ${g}, ${b}, ${color.a})`;
}
